import {
  LinkIcon,
  RefreshIcon,
  StarIcon,
  TagIcon,
} from "@heroicons/react/outline";
import { HttpError, useOne } from "@refinedev/core";
import { Calendar, Location } from "components/customIcons";
import Dropzone from "components/dropzone";
import Input from "components/input";
import { Label } from "components/label";
import Select from "components/select";
import { FrequencyOfSellType, IAttractionBuildEventPageForm, IEventDetail, SupportedLanguage } from "interfaces";
import { get } from "lodash";
import moment from "moment";
import { useEffect, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { Link } from "react-router-dom";
import {
  COUNTRY_OPTIONS,
  CURRENCIES,
  FREQUENCY_OF_SELL_TYPES,
  PROVINCE,
  SPECIAL_TYPES,
  TIMEZONES,
} from "utills/constants";
import { _generateSlug, getFormattedDate, isOpenRegistration } from "utills/helpers";
import CONFIG from "app-config/config.json";
import DateTimePicker from "components/datetimePicker";
import { SwitchButton } from "components/switchButton";
import { Plus, X } from "lucide-react";

export const EventInfo = ({ isEdit, id, eventDetailData }: { isEdit: boolean, id: string | null, eventDetailData: IEventDetail | null }) => {
  const [holidayDate, setHolidayDate] = useState("");
  const [holidayName, setHolidayName] = useState("");
  const {
    control,
    register,
    getFieldState,
    getValues,
    setValue,
    watch,
    formState: { errors },
  } = useFormContext<IAttractionBuildEventPageForm>(); // retrieve all hook methods

  const [trigger, setTrigger] = useState(false);

  const { data: categoryData, isLoading: isLoadingCategories } = useOne<
    any[],
    HttpError
  >({
    resource: "content/dropdown",
    id: "categories?category_type=attractions",
    meta: {
      resource: "data",
    },
    queryOptions: {
      enabled: !!(trigger || id),
    } as any,
  });
  const { data: eventData, isLoading: isLoadingEvents } = useOne<any[],HttpError>({
    resource: "content/dropdown",
    id: "events?v=2&event_type=attractions",
    meta: {
      resource: "data",
    },
    queryOptions: {
      enabled: !!((trigger || id) && watch("event_info.frequency_of_sell")),
    } as any,
  });

  const _renderGenerateSlugButton = () => {
    return (
      (getValues("languages").includes(SupportedLanguage.EN) || getValues("languages").includes(SupportedLanguage.VI)) 
      && (getValues("multilingual_content")?.name?.en || getValues("multilingual_content")?.name?.vi)
    ) ? (
      <div className="w-8 h-full flex items-center justify-center absolute top-1/2 right-1 -translate-y-1/2 cursor-pointer">
        <RefreshIcon
          className="w-5 text-brand-500"
          onClick={() =>
            setValue(
              "event_info.event_slug",
              _generateSlug(
                getValues(`multilingual_content.name.en`) || getValues(`multilingual_content.name.vi`)
              ),
              {shouldValidate: true}
            )
          }
        />
      </div>
    ) : <></>
  }

  const provinceIds = PROVINCE.map((proinvce) => ({
    value: proinvce.value,
    label: proinvce.name_en,
  }));

  const categoryIds = (categoryData?.data ?? []).map((category) => ({
    value: category.id,
    label: category.name_en,
  }));

  const eventIds = (eventData?.data ?? []).map((event) => ({
    value: event.id,
    label: event.name_en,
  }));

  const timezoneArr = Object.keys(TIMEZONES as any).map(
    (key: string, index: number) => ({
      value: key,
      label: get(TIMEZONES, `${key}.value`, ""),
    })
  );

  useEffect(() => {
    if (isEdit && !trigger) {
      setTrigger(true);
    }
  }, [isEdit, trigger]);

  return (
    <div className="box-container p-4 sm:p-6">
      {!isEdit ? (
        (!getFieldState("event_info").isTouched || errors["event_info"]) && !id ? (
          <p>
            Provide details about your upcoming event, such as the date, time,
            location, and a brief description. This information will help
            attendees know what to expect and allow them to easily add the event
            to their calendars.
          </p>
        ) : (
          <div className="flex flex-col">
            <div className="relative w-full overflow-hidden">
              <img
                src={getValues("event_info.banner_url")}
                alt={`${getValues("event_info.banner_url")} Banner`}
                className="rounded-lg"
              />
            </div>
            <div className="flex flex-col justify-center items-start mt-4">
              <div className="w-full flex flex-row items-start gap-x-2">
                <div className="w-40 shrink-0 flex flex-row items-center gap-x-2">
                  <Calendar />
                  Event date time:
                </div>
                <p>
                  {moment(getValues("event_info.start_date")).format(
                    "HH:mm DD/MM/YYYY"
                  )}{" "}
                  -{" "}
                  {moment(getValues("event_info.end_date")).format(
                    "HH:mm DD/MM/YYYY"
                  )}{" "}
                  <span className="mx-2">•</span>
                  {
                    timezoneArr.find(
                      (timezone) =>
                        timezone.value === getValues("event_info.timezone")
                    )?.label
                  }{" "}
                  <span className="mx-2">•</span>{" "}
                  {
                    CURRENCIES.find(
                      (currency) =>
                        currency.value === getValues("event_info.currency")
                    )?.label
                  }
                </p>
              </div>
              <div className="w-full flex flex-row items-start gap-x-2 mt-2">
                <div className="w-40 shrink-0 flex flex-row items-center gap-x-2">
                  <Location />
                  Event location:
                </div>
                <p>
                  {
                    provinceIds.find(
                      (province) =>
                        province.value === getValues("event_info.venue")
                    )?.label
                  }{getValues("event_info.venue") ? "," : ""} {getValues("event_info.country")}
                </p>
              </div>
              <div className="w-full flex flex-row items-start gap-x-2 mt-2">
                <div className="w-40 shrink-0 flex flex-row items-center gap-x-2">
                  <LinkIcon className="w-4 h-4" />
                  Event link
                </div>
                <Link
                  to={`${CONFIG.DOMAIN}/event/${getValues(
                    "event_info.event_slug"
                  )}`}
                  target="_blank"
                  className="hover:underline text-brand-500"
                  style={{wordBreak: "break-word"}}
                >
                  {CONFIG.DOMAIN}/event/{getValues("event_info.event_slug")}
                </Link>
              </div>
              <div className="w-full flex flex-row items-start gap-x-2 mt-2">
                <div className="w-40 shrink-0 flex flex-row items-center gap-x-2">
                  <StarIcon className="w-4 h-4" />
                  Event categories:
                </div>
                <div className="flex flex-row items-center flex-wrap gap-x-2" style={{wordBreak: "break-word"}}>
                  {categoryIds
                    .filter((category) =>
                      getValues("event_info").event_category_ids.includes(
                        category.value
                      )
                    )
                    .map((category, index) => (
                      <p key={category.value}>
                        {index !== 0 ? <span className="mr-2">•</span> : ""}
                        {category.label}
                      </p>
                    ))}
                </div>
              </div>
              <div className="w-full flex flex-row items-start gap-x-2 mt-2">
                <div className="w-40 shrink-0 flex flex-row items-center gap-x-2">
                  <TagIcon className="w-4 h-4" />
                  Event tag:
                </div>
                <p>
                  {
                    SPECIAL_TYPES.find(
                      (type) =>
                        type.value === getValues("event_info.special_type")
                    )?.label
                  }
                </p>
              </div>
              {watch("event_info.frequency_of_sell") === FrequencyOfSellType.ONCE ? (
                <div className="w-full flex flex-row items-start gap-x-2 mt-2">
                  <div className="w-40 shrink-0 flex flex-row items-center gap-x-2">
                    <Calendar className="w-4 h-4" />
                    Other event dates:
                  </div>
                  <div style={{wordBreak: "break-word"}}>
                    {eventIds
                      .filter((event) =>
                        watch("event_info").other_event_dates.includes(
                          event.value
                        )
                      )
                      .map((event, index) => (
                        <p key={event.value}>
                          {event.label}
                        </p>
                      ))}
                  </div>
                </div>
              ) : <></>}
            </div>
          </div>
        )
      ) : (
        <div className="grid gap-x-4 gap-y-6 sm:grid-cols-6 lg:gap-x-6">
          <div className="col-span-full">
            <div>
              <Label id="event_info.banner_url" name="Event Banner" />
              <Controller
                control={control}
                name={`event_info.banner_url`}
                render={({ field: { onChange, value = "" } }) => {
                  return (
                    <Dropzone
                      id={`event_info.banner_url`}
                      validator={{
                        width: 1440,
                        height: 600,
                      }}
                      onChange={(imageUrl: string) => onChange(imageUrl)}
                      value={value}
                    />
                  );
                }}
              />
              <p className="mt-1 text-sm text-[#868E95]">
                Recommended image size: 1440 x 600px <span className="mx-2">•</span> Maximum file size: 2MB <span className="mx-2">•</span> Supported image files: PNG, JPEG, JPG
              </p>
            </div>
            {get(errors, `event_info.banner_url`, "") ? (
              <p className="error-text">
                <span className="font-medium">
                  {get(errors, `event_info.banner_url.message`, "")}
                </span>
              </p>
            ) : null}
          </div>
          <div className="sm:col-span-3">
            <Label required id="event_info.timezone" name="Timezone" />
            <Controller
              control={control}
              name={`event_info.timezone`}
              render={({ field: { onChange, value = "", ref } }) => {
                return (
                  <Select
                    key={`timezone`}
                    inputRef={ref}
                    options={timezoneArr}
                    id={`event_info.timezone`}
                    placeholder="Select Timezone"
                    isDisabled={true}
                    defaultValue={
                      timezoneArr.find(
                        (selected: { label: string; value: any }) =>
                          selected.value === value
                      ) ?? ""
                    }
                    value={timezoneArr.find(
                      (selected: { label: string; value: any }) =>
                        selected.value === value
                    )}
                    onChange={(selected: { label: string; value: any }) => {
                      onChange(selected?.value);
                    }}
                    error={get(errors, `event_info.timezone`, "")}
                  />
                );
              }}
            />
            {get(errors, `event_info.timezone`, "") ? (
              <p className="error-text">
                <span className="font-medium">
                  {get(errors, `event_info.timezone.message`, "")}
                </span>
              </p>
            ) : null}
          </div>
          <div className="sm:col-span-3">
            <Label required id="event_info.currency" name="Event Currency" />
            <Controller
              control={control}
              name={`event_info.currency`}
              render={({ field: { onChange, value = "", ref } }) => {
                return (
                  <Select
                    key={`currency`}
                    inputRef={ref}
                    options={CURRENCIES}
                    id={`event_info.currency`}
                    placeholder="Select Currency"
                    isDisabled={isOpenRegistration(eventDetailData?.open_registration_date)}
                    defaultValue={
                      CURRENCIES.find(
                        (selected: { label: string; value: string }) =>
                          selected.value === value
                      ) ?? ""
                    }
                    value={CURRENCIES.find(
                      (selected: { label: string; value: string }) =>
                        selected.value === value
                    )}
                    onChange={(selected: { label: string; value: string }) => {
                      onChange(selected?.value);
                    }}
                    error={get(errors, `event_info.currency`, "")}
                  />
                );
              }}
            />
            {get(errors, `event_info.currency`, "") ? (
              <p className="error-text">
                <span className="font-medium">
                  {get(errors, `event_info.currency.message`, "")}
                </span>
              </p>
            ) : null}
          </div>
          <div className="sm:col-span-3">
            <Label
              required
              id="event_info.start_date"
              name="Event start date"
            />
            <Controller
              control={control}
              name="event_info.start_date"
              render={({ field: { onChange, value = "" } }) => {
                return (
                  <DateTimePicker
                    id="event_info.start_date"
                    placeholder="Event start date"
                    className="bg-white"
                    error={get(
                      errors,
                      `event_info.start_date.message`,
                      ""
                    )}
                    value={value}
                    onChange={(date: any) => date === null ? onChange("") : onChange(moment(date).format("YYYY-MM-DD HH:mm:ss"))}
                  />
                );
              }}
            />
            {get(errors, `event_info.start_date`, "") ? (
              <p className="error-text">
                <span className="font-medium">
                  {get(errors, `event_info.start_date.message`, "")}
                </span>
              </p>
            ) : null}
          </div>
          <div className="sm:col-span-3">
            <Label required id="event_info.end_date" name="Event end date" />
            <Controller
              control={control}
              name="event_info.end_date"
              render={({ field: { onChange, value = "" } }) => {
                return (
                  <DateTimePicker
                    id="event_info.end_date"
                    placeholder="Event end date"
                    className="bg-white"
                    error={get(
                      errors,
                      `event_info.end_date.message`,
                      ""
                    )}
                    value={value}
                    onChange={(date: any) => date === null ? onChange("") : onChange(moment(date).format("YYYY-MM-DD HH:mm:ss"))}
                  />
                );
              }}
            />
            {get(errors, `event_info.end_date`, "") ? (
              <p className="error-text">
                <span className="font-medium">
                  {get(errors, `event_info.end_date.message`, "")}
                </span>
              </p>
            ) : null}
          </div>
          <div className="sm:col-span-3">
            <Label required id="event_info.country" name="Event Country" />
            <Controller
              control={control}
              name={`event_info.country`}
              render={({ field: { onChange, value = "", ref } }) => {
                return (
                  <Select
                    key={`country`}
                    inputRef={ref}
                    options={COUNTRY_OPTIONS}
                    id={`event_info.country`}
                    placeholder="Select Country"
                    defaultValue={
                      COUNTRY_OPTIONS.find(
                        (selected: { label: string; value: string }) =>
                          selected.value === value
                      ) ?? ""
                    }
                    value={COUNTRY_OPTIONS.find(
                      (selected: { label: string; value: string }) =>
                        selected.value === value
                    )}
                    onChange={(selected: { label: string; value: string }) => {
                      onChange(selected?.value);
                      if (selected?.value !== "Vietnam") {
                        setValue("event_info.venue", "", {shouldValidate: true})
                      }
                    }}
                    error={get(errors, `event_info.country`, "")}
                  />
                );
              }}
            />
            {get(errors, `event_info.country`, "") ? (
              <p className="error-text">
                <span className="font-medium">
                  {get(errors, `event_info.country.message`, "")}
                </span>
              </p>
            ) : null}
          </div>
          <div className="sm:col-span-3">
            <Label required id="event_info.venue" name="Event Venue" />
            <Controller
              control={control}
              name={`event_info.venue`}
              render={({ field: { onChange, value = "", ref } }) => {
                return (
                  <Select
                    key={`venue__${watch("event_info.country")}`}
                    inputRef={ref}
                    options={watch("event_info.country") === "Vietnam" ? provinceIds : []}
                    id={`event_info.venue`}
                    placeholder="Select Venue"
                    defaultValue={
                      provinceIds.find(
                        (selected: { label: string; value: string }) =>
                          selected.value === value
                      ) ?? ""
                    }
                    value={provinceIds.find(
                      (selected: { label: string; value: string }) =>
                        selected.value === value
                    )}
                    onChange={(selected: { label: string; value: string }) => {
                      onChange(selected?.value);
                    }}
                    error={get(errors, `event_info.venue`, "")}
                  />
                );
              }}
            />
            {get(errors, `event_info.venue`, "") ? (
              <p className="error-text">
                <span className="font-medium">
                  {get(errors, `event_info.venue.message`, "")}
                </span>
              </p>
            ) : null}
          </div>
          <div className="sm:col-span-3">
            <Label 
              required
              id="event_info.event_slug"
              name="Event Link"
              hint="Set up a link for your Event Detail page on ActiUp Web. Format: text-text-text-.... Ex: giai-chay-actiup"
            />
            <div className="flex">
              <span className="inline-flex items-center rounded-l-md border border-r-0 border-gray-300 bg-gray-100 px-3 text-gray-500 sm:text-sm">
                {CONFIG.DOMAIN}/event/
              </span>
              <div className="relative flex-1">
                <Input
                  id="event_info.event_slug"
                  className="rounded-l-none pr-10"
                  placeholder="slug-name"
                  maxLength={100}
                  error={errors["event_info"]?.event_slug}
                  {...register("event_info.event_slug")}
                />
                {_renderGenerateSlugButton()}
              </div>
            </div>
            {get(errors, `event_info.event_slug`, "") ? (
              <p className="error-text">
                <span className="font-medium">
                  {get(errors, `event_info.event_slug.message`, "")}
                </span>
              </p>
            ) : null}
          </div>
          <div className="sm:col-span-3">
            <Label 
              id="categories"
              name="Event Categories"
              hint="Used for event categorization on ActiUp web"
            />
            <Controller
              control={control}
              name="event_info.event_category_ids"
              defaultValue={[]}
              render={({ field: { onChange, value = [], ref } }) => (
                <Select
                  isMulti
                  key={`categories`}
                  inputRef={ref}
                  options={categoryIds}
                  id="event_ids"
                  placeholder="Select Categories"
                  value={value.map((id: string) => {
                    return categoryIds.find((item) => item.value === id);
                  })}
                  onChange={(selected: { label: string; value: string }[]) =>
                    onChange(selected.map((item) => `${item.value}`))
                  }
                  error={errors["event_info"]?.event_category_ids}
                  isLoading={isLoadingCategories}
                />
              )}
            />
            {errors["event_info"]?.event_category_ids ? (
              <p className="error-text">
                <span className="font-medium">
                  {errors["event_info"]?.event_category_ids.message}
                </span>
              </p>
            ) : null}
          </div>
          <div className="sm:col-span-3">
            <Label required id="event_info.frequency_of_sell" name="Event Type" />
            <Controller
              control={control}
              name={`event_info.frequency_of_sell`}
              render={({ field: { onChange, value = "", ref } }) => {
                return (
                  <Select
                    key={`frequency_of_sell`}
                    inputRef={ref}
                    options={FREQUENCY_OF_SELL_TYPES}
                    id={`event_info.frequency_of_sell`}
                    placeholder="Select Event Type"
                    defaultValue={
                      FREQUENCY_OF_SELL_TYPES.find(
                        (selected: { label: string; value: string }) =>
                          selected.value === value
                      ) ?? ""
                    }
                    value={FREQUENCY_OF_SELL_TYPES.find(
                      (selected: { label: string; value: string }) =>
                        selected.value === value
                    )}
                    onChange={(selected: { label: string; value: string }) => {
                      onChange(selected?.value);
                      setValue("event_info.other_event_dates", []);
                      if (selected?.value !== FrequencyOfSellType.USUALLY) {
                        setValue("event_info.date_pricing.enabled", false);
                      }
                    }}
                    error={get(errors, `event_info.frequency_of_sell`, "")}
                  />
                );
              }}
            />
            {get(errors, `event_info.frequency_of_sell`, "") ? (
              <p className="error-text">
                <span className="font-medium">
                  {get(errors, `event_info.frequency_of_sell.message`, "")}
                </span>
              </p>
            ) : null}
          </div>

          {/* Date Pricing setup UI */}
          {watch("event_info.frequency_of_sell") === FrequencyOfSellType.USUALLY && (
            <div className="sm:col-span-6 mt-4">
              <Label id="event_info.date_pricing" name="Date Pricing Holidays" />
              <Controller
                control={control}
                name="event_info.date_pricing.enabled"
                render={({ field: { onChange, value } }) => (
                  <div className="my-4">
                    <SwitchButton
                      label="Enable Date Pricing"
                      checked={!!value}
                      onChange={onChange}
                    />
                  </div>
                )}
              />
              {/* Holiday input fixed, add and show tags */}
              <Controller
                control={control}
                name="event_info.date_pricing.holidays"
                render={({ field: { onChange, value = [] } }) => {
                  // Show tags above
                  return (
                    <div>
                      <div className="flex flex-wrap gap-2 mb-4">
                        {value.map((holiday: any, idx: number) => (
                          <div
                            key={idx}
                            className="group flex items-center gap-2 bg-white border border-brand-200 text-gray-700 rounded-full px-3 py-1.5 shadow-sm hover:shadow-md transition-shadow"
                          >
                            <span className="flex items-center gap-1.5 text-xs font-medium">
                              <Calendar className="size-3 text-brand-600" />
                              <span className="text-brand-600 font-semibold">{getFormattedDate(holiday.date)}</span>
                              <span className="text-gray-400">·</span>
                              <span className="text-gray-600">{holiday.name}</span>
                            </span>
                            <button
                              type="button"
                              className="flex items-center justify-center w-4 h-4 rounded-full text-gray-400 hover:bg-red-100 hover:text-red-500 transition-colors"
                              onClick={() => {
                                const newHolidays = value.filter(
                                  (_: any, i: number) => i !== idx,
                                );
                                onChange(newHolidays);
                              }}
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                      <div className="w-full flex flex-col sm:flex-row sm:items-center gap-4">
                        <div className="flex-1">
                          <DateTimePicker
                            id="event_info.date_pricing.holiday_input_date"
                            value={holidayDate}
                            onChange={(date: any) =>
                              setHolidayDate(
                                date ? moment(date).format("YYYY-MM-DD") : "",
                              )
                            }
                          />
                        </div>
                        <div className="flex-1">
                          <Input
                            id="event_info.date_pricing.holiday_input_name"
                            value={holidayName}
                            placeholder="Holiday name"
                            onChange={(e: { target: { value: string } }) =>
                              setHolidayName(e.target.value)
                            }
                          />
                        </div>
                        <button
                          type="button"
                          className="flex items-center justify-center text-center gap-2 h-11 bg-brand-500 text-white px-3 py-1 rounded-md text-sm"
                          onClick={() => {
                            if (holidayDate && holidayName) {
                              onChange([
                                ...value,
                                { date: holidayDate, name: holidayName },
                              ]);
                              setHolidayDate("");
                              setHolidayName("");
                            }
                          }}
                        >
                        <Plus size={12} className="text-white" /> Thêm
                        </button>
                      </div>
                    </div>
                  );
                }}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};
